<template>
  <v-app-bar flat id="AppBar" :class="isOnTop ? 'AppBarOnTop' : ''">
    <transition name="scroll-x-reverse-transition" mode="out-in">
      <v-btn v-if="!appStore.getNav && $vuetify.display.mobile" icon @click="goBackOrToDefault">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-app-bar-title v-else-if="$vuetify.display.mobile">
        <span class="font-weight-bold">ULTIKITS</span>
      </v-app-bar-title>
      <v-btn v-else @click="$router.push('/')" class="ml-4 mr-4 mt-1">
        <h3>ULTIKITS</h3>
      </v-btn>
    </transition>
    <v-btn v-if="$vuetify.display.mdAndUp" prepend-icon="mdi-server" to="/server" class="mr-1">{{ t('$vuetify.app.appBar.server') }}</v-btn>
    <v-btn v-if="$vuetify.display.mdAndUp" prepend-icon="mdi-crown" to="/member" class="mr-1">{{ t('$vuetify.app.appBar.member') }}</v-btn>
    <v-btn v-if="$vuetify.display.mdAndUp" prepend-icon="mdi-package" to="/module" class="mr-1">{{ t('$vuetify.app.appBar.module') }}</v-btn>
    <v-btn v-if="$vuetify.display.mdAndUp" prepend-icon="mdi-xml" to="/developer" class="mr-1">{{ t('$vuetify.app.appBar.dev') }}</v-btn>
    <v-btn v-if="$vuetify.display.mdAndUp && userStore.isAdmin" prepend-icon="mdi-shield-crown" to="/admin" class="mr-1" color="amber-darken-2">{{ ti('nav.admin') }}</v-btn>
    <v-menu>
      <template v-slot:activator="{ props: menuProps }">
        <v-btn v-if="$vuetify.display.mdAndUp" v-bind="menuProps" icon="mdi-translate" size="small" class="mr-1"></v-btn>
      </template>
      <v-list density="compact" :nav="true">
        <v-list-item @click="switchLang('zh')">
          <v-list-item-title>中文</v-list-item-title>
        </v-list-item>
        <v-list-item @click="switchLang('en')">
          <v-list-item-title>English</v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>
    <v-spacer/>
    <v-btn icon="mdi-plus"></v-btn>
    <v-menu>
      <template v-slot:activator="{ props }">
        <v-btn v-bind="props" prepend-icon="mdi-account" append-icon="mdi-menu-down">{{ userStore.getUsername }}</v-btn>
      </template>
      <v-list density="compact" :nav="true">
        <v-list-item link to="/profile">
          <v-list-item-title>{{ t('$vuetify.app.appBar.profile') }}</v-list-item-title>
        </v-list-item>
        <v-divider class="mb-1"/>
        <v-list-item @click="logout" :link="true" color="red" append-icon="mdi-logout" :title="t('$vuetify.app.appBar.logout')"/>
      </v-list>
    </v-menu>
    <v-expand-transition>
      <v-progress-linear
        v-if="appStore.getBl"
        :active="appStore.getBl"
        :indeterminate="appStore.getBl"
        absolute
        bottom
        color="indigo"
      ></v-progress-linear>
    </v-expand-transition>
  </v-app-bar>
</template>

<script lang="ts" setup>
  import {ref} from "vue";
  import {useGuard} from "@this-dot/vue-route-guard";

  import {useUserStore} from "@/store/user";
  import {removeToken, removeUser} from "@/app/utils/store";
  import {useAppStore} from "@/store/app";
  import {useToast} from "vue-toastification";
  import {useRouter} from "vue-router";
  import { Crisp } from "crisp-sdk-web"
  import {useLocale} from "vuetify";
  import { useI18n } from 'vue-i18n'
  import { setLocale } from '@/locales'

  const userStore = useUserStore()
  const appStore = useAppStore()

  const auth = useGuard()
  const toast = useToast()
  const router = useRouter()
  const { t } = useLocale()
  const { t: ti } = useI18n()

  const switchLang = (lang: 'zh' | 'en') => {
    setLocale(lang)
    window.location.reload()
  }

  const isOnTop = ref(true)

  const logout = () => {
    appStore.setLoading(true)
    auth.clearAuthentication().then(() => {
      removeToken()
      removeUser()
      Crisp.setTokenId();
      Crisp.session.reset();
      appStore.setRedirect("/")
      appStore.setLoading(false)
      toast.success(t('$vuetify.app.appBar.logoutMsg'))
    })
  }

  window.addEventListener("scroll", () => {
    isOnTop.value = window.scrollY <= 0
  });

  function goBackOrToDefault() {
    // 不能先 go(-1) 再检查是否"真的"返回了：createWebHistory 下 go() 依赖浏览器原生
    // popstate 事件才会更新 currentRoute / history.location，而 popstate 是异步触发的
    // （不是微任务，是下一个宏任务），所以调用后立即读取两者仍是旧值、恒相等，
    // 会导致每次点击都误判"没有前一页"，进而并发触发一次多余的 router.replace('/')。
    //
    // Vue Router 4 在每次 push/replace 时都会把 { back, current, forward, position }
    // 写入 history.state（对应浏览器的 history.state），其中 back 就是"上一页"的位置，
    // 且是在导航完成时同步写入的，读取它不存在上述竞态。因此改为先同步判断是否存在
    // 上一页，再二选一地发起导航，而不是"先动作、后纠正"。
    const historyState = router.options.history.state
    const hasPreviousPage = historyState.back !== null && historyState.back !== undefined

    if (hasPreviousPage) {
      // 存在上一页，安全返回
      router.go(-1)
    } else {
      // 没有前一页，返回到默认页面
      router.replace('/')
    }
    appStore.setNav(true)
  }
</script>
